/**
 * Where "Home" (and so the first screen after sign-in) is for this person,
 * from their player hats (`player.ts`, `playerProfile(me).primary`):
 *
 * - anyone who works on projects — an owner or developer, a contractor's or
 *   subcontractor's people, a consultant, a QS — keeps the personal home
 *   (their projects and today), whatever else they also are;
 * - a supplier (and nothing else) → their supplier portal's dashboard, even
 *   with a dormant personal organisation or a set-up left half-way;
 * - a land seller (and nothing else) → their listings;
 * - a worker (a claimed worker record, or no KorOS Build access) → `/me/work`;
 * - a provider (services offered, no project and no worker record) → `/me/services`;
 * - a sole trader without KorOS Build access → their sole-trader project (its
 *   usual landing), or Projects when there are several (`soleTraderLanding`);
 * - a newcomer (no hat at all) → the set-up they left half-way, or the set-up
 *   wizard; KorOS staff stay home.
 *
 * Decided from `/me`; only a newcomer costs a request (the onboarding overview,
 * and the worker record when `/me` predates the `worker` flag). Offline or on
 * any error it stays on the personal home: a landing rule must never lock
 * anyone out of the app.
 */
import type { Me } from '@/lib/api/types';

import { projectLanding, type ProjectLanding } from './landing';
import { playerProfile, soleTraderProjectIds } from './player';

export type HomeTarget =
  | { to: '/supplier/$orgId/dashboard'; params: { orgId: string } }
  | { to: '/land/sell/$orgId'; params: { orgId: string } }
  | { to: '/me/work' }
  | { to: '/me/services' }
  | { to: '/projects' }
  | { to: ProjectLanding; params: { projectId: string } }
  | { to: '/onboarding' }
  | { to: '/onboarding/$flow'; params: { flow: string } };

export type LandingProbes = {
  /** Does this person have a worker record (engagements)? Only for an old cached `/me`. */
  hasWorkerRecord: () => Promise<boolean>;
  /** The onboarding overview: a setup to resume, or whether to suggest one. */
  onboarding: () => Promise<{ resume: string | null; suggest: boolean }>;
};

/** The supplier company whose portal is this person's home, if they only sell. */
export function supplierHome(me: Me | null): string | null {
  const profile = playerProfile(me);
  if (profile.primary !== 'supplier') return null;
  return profile.organizations.find((org) => org.kind === 'supplier')?.id ?? null;
}

/**
 * Where a sole trader without KorOS Build access lands: their one sole-trader
 * project (its usual first screen), Projects when there are several, nothing
 * when they have none. Pure, so the shell can use it offline too.
 */
export function soleTraderLanding(me: Me | null): HomeTarget | null {
  const projects = soleTraderProjectIds(me);
  if (projects.length === 0) return null;
  if (projects.length > 1) return { to: '/projects' };
  const projectId = projects[0] ?? '';
  return { to: projectLanding(me, projectId), params: { projectId } };
}

export async function homeLanding(
  me: Me | null,
  probes: LandingProbes,
): Promise<HomeTarget | null> {
  if (!me) return null;
  const profile = playerProfile(me);
  const first = (kind: string) => profile.organizations.find((org) => org.kind === kind)?.id;
  switch (profile.primary) {
    case 'home':
      return null;
    case 'supplier': {
      const orgId = first('supplier');
      return orgId ? { to: '/supplier/$orgId/dashboard', params: { orgId } } : null;
    }
    case 'land_seller': {
      const orgId = first('land_seller');
      return orgId ? { to: '/land/sell/$orgId', params: { orgId } } : null;
    }
    case 'worker':
      return { to: '/me/work' };
    case 'provider':
      return { to: '/me/services' };
    case 'sole_trader':
      return soleTraderLanding(me) ?? { to: '/me/services' };
    case 'newcomer':
      break;
  }
  try {
    if (me.worker === undefined && (await probes.hasWorkerRecord())) return { to: '/me/work' };
    const onboarding = await probes.onboarding();
    if (onboarding.resume) return { to: '/onboarding/$flow', params: { flow: onboarding.resume } };
    if (onboarding.suggest) return { to: '/onboarding' };
  } catch {
    return null;
  }
  return null;
}
